import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlayerStore, selectEffectiveTier } from '../store/playerStore';
import { getFlightLog, saveBattleReport } from '../services/flightLog.service';
import ShipManifestList from '../components/ShipManifestList';
import BattleReportView from '../components/BattleReportView';
import { Button } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import { Skeleton } from '../components/ui/Skeleton';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import ResourceQuantities from '../components/ResourceQuantities';
import { hasResourceQuantities } from '../utils/resourceQuantities';
import { Icon } from '../components/ui/Icon';
import { formatCoords, formatDateTime } from '../utils/fleetFormat';
import { FLEET_MISSION_DEFINITIONS, getSavedBattleReportLimit, type FlightLog } from '@kaeliux/shared';

/**
 * Battle Reports page (`/fleets/battle-report/:logId`) — Attack and Pirate
 * arrival logs link here instead of opening the generic details modal.
 * When the log carries a `battleReport` (#18/#19), renders the full
 * round-by-round combat resolution via `BattleReportView`; older records
 * and Sun Support (still a no-op stub — team play doesn't exist yet) fall
 * back to the plain flight-log summary below.
 */
export default function BattleReportPage() {
    const { logId } = useParams<{ logId: string }>();
    const { profile } = useAuthStore();
    const { premium, loadPlayerState } = usePlayerStore();
    const effectiveTier = usePlayerStore(selectEffectiveTier);

    const [log, setLog] = useState<FlightLog | null | undefined>(undefined);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!profile?.id || !logId) return;
        getFlightLog(profile.id, logId)
            .then(setLog)
            .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load report'));
    }, [profile?.id, logId]);

    // A direct link/refresh may land here before anything else has primed
    // usePlayerStore — the effective tier drives the saved-report limit below.
    useEffect(() => {
        if (!profile?.id || !profile.mainPlanetId || premium !== null) return;
        loadPlayerState(profile.id, profile.mainPlanetId).catch(() => { });
    }, [profile?.id, profile?.mainPlanetId, premium, loadPlayerState]);

    async function handleToggleSave() {
        if (!log) return;
        setIsSaving(true);
        setSaveError(null);
        try {
            await saveBattleReport(log.id, !log.saved);
            setLog({ ...log, saved: !log.saved });
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to update saved state');
        } finally {
            setIsSaving(false);
        }
    }

    if (!profile || !profile.race) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <Skeleton variant="text" width={160} height={28} />
            </div>
        );
    }

    if (log === undefined) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <Skeleton variant="text" width={180} height={28} />
            </div>
        );
    }

    if (loadError || !log) {
        return (
            <main className="max-w-3xl mx-auto p-4">
                <Panel>
                    <h2 className="text-title font-bold mb-2">Report not found</h2>
                    <p className="text-content-muted mb-6">
                        {loadError ?? "This battle report doesn't exist, or has aged out of the archive."}
                    </p>
                    <Link to="/fleets" className={linkButtonClasses('secondary', 'sm')}>← Back to Fleets</Link>
                </Panel>
            </main>
        );
    }

    const mission = FLEET_MISSION_DEFINITIONS[log.mission];
    const resources = log.resources && hasResourceQuantities(log.resources) ? log.resources : null;
    const limit = getSavedBattleReportLimit(effectiveTier);

    return (
        <main className="max-w-3xl mx-auto p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-title font-bold text-content-primary">
                    {mission.name} Report — {log.event === 'arrival' ? 'Arrived' : 'Returned'}
                </h1>
                <Link to="/fleets" className={linkButtonClasses('secondary', 'sm')}>← Back to Fleets</Link>
            </div>

            <Panel>
                {!log.battleReport && (
                    <p className="text-status-warning text-sm font-semibold" data-testid="battle-report-stub-notice">
                        No detailed combat log for this report.
                    </p>
                )}
                <p className="text-content-secondary text-sm italic">{log.message}</p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div>
                        <span className="text-content-muted block">Route</span>
                        <span className="text-content-primary">{formatCoords(log.origin)} → {formatCoords(log.target)}</span>
                    </div>
                    <div>
                        <span className="text-content-muted block">Departed</span>
                        <span className="text-content-primary">{formatDateTime(log.departedAt)}</span>
                    </div>
                    <div>
                        <span className="text-content-muted block">{log.event === 'arrival' ? 'Arrived' : 'Returned'}</span>
                        <span className="text-content-primary">{formatDateTime(log.occurredAt)}</span>
                    </div>
                    {resources && (
                        <div>
                            <span className="text-content-muted block">Resources</span>
                            <span className="text-content-primary"><ResourceQuantities amounts={resources} /></span>
                        </div>
                    )}
                </div>

                {log.battleReport ? (
                    <BattleReportView report={log.battleReport} viewerUserId={profile.id} />
                ) : (
                    <div>
                        <h3 className="text-content-secondary text-sm font-semibold mb-1">Ships</h3>
                        <ShipManifestList ships={log.ships} race={profile.race} />
                    </div>
                )}

                <div className="flex flex-col gap-1.5 pt-2 border-t border-hairline">
                    <div className="flex items-center gap-3">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleToggleSave}
                            disabled={isSaving}
                            loading={isSaving}
                            data-testid="save-report-button"
                        >
                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                <Icon name="star" size={14} fill={log.saved ? 'currentColor' : 'none'} aria-hidden="true" />
                                {log.saved ? 'Saved' : 'Save report'}
                            </span>
                        </Button>
                        <span className="text-content-muted text-xs">
                            You can save up to {limit} battle reports on your current tier.
                        </span>
                    </div>
                    {saveError && <p className="text-status-danger text-xs" data-testid="save-error">{saveError}</p>}
                </div>
            </Panel>
        </main>
    );
}
